import React, { useEffect, useState } from 'react';
import moment from 'moment';
import { useSelector, useDispatch } from 'react-redux';
import { Form, Button } from 'antd';
import { states } from '../../constants/states';
import { fieldNames, formFields, periods } from './constants';
import { getDates, setInitPeriod } from './utils';
import {
  setCalendarSheet,
  setLocalTask,
} from '../../store/actions/calendarActions';
import useAddTaskMutation from '../../mutations/useAddTaskMutation';
import useEditTaskMutation from '../../mutations/useEditTaskMutation';
import useSuccessTaskMutation from '../../hooks/useSuccessTaskMutation';
import styles from './ItemToCalendarForm.module.scss';

const ItemToCalendarNode = ({ form, onCancel }) => {
  const {
    resetFields,
    getFieldDecorator,
    validateFields,
    getFieldValue,
    setFieldsValue,
  } = form;

  const dispatch = useDispatch();
  const { localTask, state, calendarSheet, view } = useSelector(
    (state) => state.newCalendarStore
  );

  const isEditState = state === states.editTask;

  const handleSuccess = useSuccessTaskMutation(
    isEditState ? 'Изменения сохранены' : 'Заявка добавлена в календарь'
  );

  const { mutate: addTask, isLoading: isAddTaskLoading } = useAddTaskMutation({
    onSuccess: handleSuccess,
  });

  const { mutate: editTask, isLoading: isEditTaskLoading } =
    useEditTaskMutation({
      onSuccess: handleSuccess,
    });

  const handleCancel = () => {
    resetFields();
    if (typeof onCancel === 'function') {
      onCancel();
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    validateFields((err) => {
      if (!err) {
        const { fromDate, toDate, requestId, worker, taskId } = localTask;

        const toDateMoment = moment(toDate);

        const values = {
          workerId: worker.id,
          requestId,
          fromDate,
          toDate:
            toDateMoment.hour() === 0
              ? toDateMoment
                  .subtract(1, 'days')
                  .set({ hour: '23', minute: '59', second: '59' })
                  .utc()
                  .format()
              : toDate,
        };

        isEditState
          ? editTask({ values: { ...values, taskId } })
          : addTask({ values });
      }
    });
  };

  const handleChange = (time, timeString, fieldName) => {
    if (time) {
      const { fromDate, toDate } = getDates({
        getFieldValue,
        time,
        timeString,
        fieldName,
      });

      if (fromDate && toDate) {
        const task = {
          ...localTask,
          fromDate: fromDate.clone().utc().format(),
          toDate: toDate.clone().utc().format(),
        };

        dispatch(setLocalTask(task));

        if (!fromDate.isSame(calendarSheet, view)) {
          dispatch(setCalendarSheet(fromDate));
        }
      }
    }
  };

  const startTime = getFieldValue(fieldNames.startTime)?.format('HH:mm');
  const endTime = getFieldValue(fieldNames.endTime)?.format('HH:mm');

  const [period, setPeriod] = useState(
    setInitPeriod(
      localTask?.fromDate ? moment(localTask.fromDate).format('HH:mm') : '',
      localTask?.toDate ? moment(localTask.toDate).format('HH:mm') : ''
    )
  );

  const handlePeriodChange = (e) => {
    const value = e.target.value;

    const startTime = moment(periods[value].start, 'HH:mm');
    const endTime = moment(periods[value].end, 'HH:mm');

    setFieldsValue({
      [fieldNames.startTime]: startTime,
      [fieldNames.endTime]: endTime,
    });

    setPeriod(value);
    handleChange(startTime, periods[value].start, fieldNames.startTime);
    handleChange(endTime, periods[value].end, fieldNames.endTime);
  };

  useEffect(() => {
    setPeriod(setInitPeriod(startTime, endTime));
  }, [startTime, endTime]);

  useEffect(() => {
    resetFields();
  }, [localTask, resetFields]);

  return (
    <Form onSubmit={handleSubmit}>
      {formFields.map((field) =>
        field.periodsField ? (
          field.renderComponent({ value: period, onChange: handlePeriodChange })
        ) : (
          <Form.Item
            key={field.name}
            label={field.label}
            labelCol={{ span: 7 }}
            wrapperCol={{ span: 17 }}
            labelAlign="left"
          >
            {getFieldDecorator(field.name, {
              initialValue: field.getInitialValue(localTask),
              rules: field.rules,
            })(
              field.renderComponent({
                onChange: (time, timeString) =>
                  handleChange(time, timeString, field.name),
                startTime: getFieldValue(fieldNames.startTime),
                endTime: getFieldValue(fieldNames.endTime),
              })
            )}
          </Form.Item>
        )
      )}
      <div className={styles.actionsWrap}>
        <Button
          type="primary"
          block
          ghost
          className={styles.action}
          onClick={handleCancel}
          disabled={isAddTaskLoading || isEditTaskLoading}
        >
          Отменить
        </Button>
        <Button
          type="primary"
          htmlType="submit"
          block
          className={styles.action}
          loading={isAddTaskLoading || isEditTaskLoading}
        >
          Сохранить
        </Button>
      </div>
    </Form>
  );
};

const ItemToCalendarForm = Form.create({ name: 'item_to_calendar' })(
  ItemToCalendarNode
);

export default ItemToCalendarForm;
